import { all, call, put, race, select, take, takeEvery } from 'redux-saga/effects'
import { replace } from 'redux-first-history'
import { List } from 'immutable'

import * as Api from 'api/gaio'
import * as AppActions from 'actions/app'
import * as Actions from 'actions/gaio'
import * as FilterActions from 'actions/filter'
import * as Selectors from 'selectors'
import { DEFAULT_GAIO_PROMPT_FREQUENCY } from 'utils/gaio'

const hasTags = tags => {
  if (!tags) {
    return false
  }

  if (typeof tags.isEmpty === 'function') {
    return !tags.isEmpty()
  }

  return tags.length > 0
}

const hasDisallowedGaioModels = (modelIds, allowedModels) => {
  if (allowedModels == null || !modelIds) {
    return false
  }

  const allowedModelIds = allowedModels.map(model => model.get('id'))

  return modelIds.some(modelId => !allowedModelIds.includes(modelId))
}

const isGaioAiProviderNotAllowedError = error => error.message === 'GAIO AI provider not allowed'

const promptFrequency = promptForm => promptForm.get('frequency') || DEFAULT_GAIO_PROMPT_FREQUENCY

const changedPromptFormField = (promptForm, originalPromptForm, field) => (
  !originalPromptForm || promptForm.get(field) !== originalPromptForm.get(field)
)

export function* initialLoad({ payload = {} } = {}) {
  const isPromptsView = payload.view === 'prompts'

  yield put(Actions.setHasProjects(null))
  const existingProjects = yield select(Selectors.getGaioProjects)
  const hasExistingProjects = existingProjects.size > 0

  if (!hasExistingProjects) {
    const projectSearchInputValue = yield select(Selectors.getGaioProjectSearchInputValue)
    const projectSearchQuery = projectSearchInputValue?.trim()
    const projectSearchPayload = projectSearchQuery?.length >= 2 ? { query: projectSearchQuery } : undefined

    yield put(Actions.searchProjectsStart(projectSearchPayload))
  }

  if (isPromptsView) {
    yield put(Actions.searchTagsStart())
  }

  if (!hasExistingProjects) {
    const { error } = yield race({
      success: take(Actions.searchProjectsSuccess),
      error: take(Actions.searchProjectsError)
    })

    if (error) {
      yield put(Actions.setHasProjects(false))
      yield put(AppActions.genericErrorMessage())

      return
    }
  }

  const projects = yield select(Selectors.getGaioProjects)
  const projectsCount = yield select(Selectors.getGaioProjectsTotal)

  if (projectsCount > 0) {
    const routeProjectId = parseInt(payload.projectId, 10)
    const selectedProject = projects.find(project => project.get('id') === routeProjectId) || projects.first()
    const selectedProjectId = selectedProject.get('id')
    const hasInvalidRouteProject = !Number.isNaN(routeProjectId) && selectedProjectId !== routeProjectId

    yield put(Actions.setSelectedProject(selectedProject.toJS()))
    yield put(Actions.setHasProjects(true))

    if (!isPromptsView) {
      yield put(Actions.fetchPromptsStatisticsStart())
    }

    if (isPromptsView) {
      if (hasInvalidRouteProject) {
        yield put(replace(`/app/geo_analysis/${selectedProjectId}/prompts`))
      }
    } else if (hasInvalidRouteProject) {
      yield put(replace(`/app/geo_analysis/${selectedProjectId}`))
    }
  } else {
    yield put(Actions.setHasProjects(false))
  }
}

export function* createProject({ payload }) {
  try {
    const analysisObjects = payload.analysisObjects.map(analysisObject => ({
      code_id: analysisObject.codeId,
      is_owned: Boolean(analysisObject.isOwned)
    }))

    const body = {
      name: payload.name,
      analysis_objects: analysisObjects,
      websites: payload.websites,
      default_country_id: payload.countryId
    }

    const result = yield call(Api.createGaioProject, body)

    if (result?.id == null) {
      throw new Error('Project could not be created')
    }

    yield put(Actions.createProjectSuccess(result))
    yield put(replace(`/app/geo_analysis/${result.id}/prompts`))
    yield put(Actions.setSelectedProject(result))
  } catch (error) {
    yield put(Actions.createProjectError(error))
    yield put(AppActions.genericErrorMessage())
    yield put(AppActions.exception(error))
  }
}

export function* updateProject({ payload } = {}) {
  try {
    const i18n = yield select(Selectors.getI18n)
    const projectForm = yield select(Selectors.getGaioProjectForm)
    const selectedProject = yield select(Selectors.getGaioSelectedProject)
    const projectId = payload?.id ?? projectForm.get('id') ?? selectedProject?.get('id')
    const name = payload?.name ?? projectForm.get('name') ?? selectedProject?.get('name')

    const body = {
      name
    }

    if (payload?.analysisObjects) {
      body.analysis_objects = payload.analysisObjects.map(analysisObject => ({
        code_id: analysisObject.codeId ?? analysisObject.id,
        is_owned: Boolean(analysisObject.isOwned)
      }))
    }

    const result = yield call(Api.updateGaioProject, projectId, body)

    yield put(Actions.updateProjectSuccess(result))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('success'),
      variant: 'success'
    }))
    yield put(Actions.searchProjectsStart())
  } catch (error) {
    yield put(Actions.updateProjectError(error))
    yield put(AppActions.genericErrorMessage())
    yield put(AppActions.exception(error))
  }
}

export function* deleteProject() {
  try {
    const i18n = yield select(Selectors.getI18n)
    const projectToDelete = yield select(Selectors.getGaioProjectToDelete)
    const isPromptsOverview = yield select(Selectors.isGeoAnalysisPromptsOverview)
    const projectId = projectToDelete.get('id')

    yield call(Api.deleteGaioProject, projectId)

    yield put(Actions.deleteProjectSuccess({ id: projectId }))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('success'),
      variant: 'success'
    }))

    const projects = yield select(Selectors.getGaioProjects)

    if (projects.size > 0) {
      const selectedProject = projects.first()
      const selectedProjectId = selectedProject.get('id')

      yield put(replace(isPromptsOverview ? `/app/geo_analysis/${selectedProjectId}/prompts` : '/app/geo_analysis'))
      yield put(Actions.setSelectedProject(selectedProject.toJS()))
      yield put(Actions.setHasProjects(true))

      if (isPromptsOverview) {
        yield put(Actions.searchPromptsStart())
      }
    } else {
      yield put(replace('/app/geo_analysis'))
      yield put(Actions.setHasProjects(false))
    }
  } catch (error) {
    yield put(Actions.deleteProjectError(error))
    yield put(AppActions.genericErrorMessage())
    yield put(AppActions.exception(error))
  }
}

export function* createPrompt() {
  const i18n = yield select(Selectors.getI18n)

  try {
    const projectId = yield select(Selectors.getGaioSelectedProjectId)
    const promptForm = yield select(Selectors.getGaioPromptForm)
    const allowedModels = yield select(Selectors.getAllowedGaioAiModels)

    if (allowedModels != null) {
      if (hasDisallowedGaioModels(promptForm.get('models'), allowedModels)) {
        throw new Error('GAIO AI provider not allowed')
      }
    }

    const body = {
      project_id: projectId,
      title: promptForm.get('title'),
      personas: promptForm.get('personas'),
      prompt: promptForm.get('prompt'),
      country_id: promptForm.get('countryId'),
      language_id: promptForm.get('languageId'),
      disabled: promptForm.get('disabled') === true,
      frequency: promptFrequency(promptForm),
      models: promptForm.get('models'),
      tags: promptForm.get('tags')
    }

    const result = yield call(Api.createGaioPrompt, body)

    yield put(Actions.createPromptSuccess(result))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('success'),
      variant: 'success'
    }))
    yield put(Actions.searchPromptsStart())
    yield put(Actions.fetchPromptsStatisticsStart())
    yield put(Actions.fetchPromptOptionsStart())
    yield put(Actions.searchTagsStart())
  } catch (error) {
    yield put(Actions.createPromptError(error))

    if (isGaioAiProviderNotAllowedError(error)) {
      yield put(AppActions.showAppSnackbarMessage({
        text: i18n.get('gaio_selected_ai_providers_not_allowed'),
        variant: 'error'
      }))
    } else {
      yield put(AppActions.genericErrorMessage())
    }

    yield put(AppActions.exception(error))
  }
}

export function* updatePrompt({ payload } = {}) {
  const i18n = yield select(Selectors.getI18n)

  try {
    let promptId = payload?.id
    let body = payload?.body

    if (!body) {
      const promptForm = yield select(Selectors.getGaioPromptForm)
      const allowedModels = yield select(Selectors.getAllowedGaioAiModels)

      if (allowedModels != null) {
        if (hasDisallowedGaioModels(promptForm.get('models'), allowedModels)) {
          throw new Error('GAIO AI provider not allowed')
        }
      }

      const originalPromptForm = yield select(Selectors.getGaioOriginalPromptForm)

      promptId = promptForm.get('id')
      body = {
        title: promptForm.get('title'),
        models: promptForm.get('models'),
        tags: promptForm.get('tags')
      }

      if (changedPromptFormField(promptForm, originalPromptForm, 'disabled')) {
        body.disabled = promptForm.get('disabled') === true
      }

      if (changedPromptFormField(promptForm, originalPromptForm, 'frequency')) {
        body.frequency = promptFrequency(promptForm)
      }
    }

    if (payload?.body?.disabled === false) {
      const monitoringUnitLimitReached = yield select(Selectors.getGaioMonitoringUnitLimitReached)

      if (monitoringUnitLimitReached) {
        yield put(Actions.updatePromptError(new Error('GAIO prompt limit reached')))

        return
      }
    }

    const result = yield call(Api.updateGaioPrompt, promptId, body)
    const successPayload = payload?.body ? { ...result, ...body, id: promptId } : result

    yield put(Actions.updatePromptSuccess(successPayload))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('success'),
      variant: 'success'
    }))
    yield put(Actions.fetchPromptOptionsStart())
    yield put(Actions.fetchPromptsStatisticsStart())

    if (hasTags(body.tags)) {
      yield put(Actions.searchTagsStart())
    }
  } catch (error) {
    yield put(Actions.updatePromptError(error))

    if (isGaioAiProviderNotAllowedError(error)) {
      yield put(AppActions.showAppSnackbarMessage({
        text: i18n.get('gaio_selected_ai_providers_not_allowed'),
        variant: 'error'
      }))
    } else if (error.response?.statusCode === 403) {
      const gaioConfig = yield select(Selectors.getGaioConfig)
      yield put(AppActions.showAppSnackbarMessage({
        text: i18n.get('gaio_general_limit_reached_text', { name: gaioConfig.get('name') || '-' }),
        variant: 'error'
      }))
    } else {
      yield put(AppActions.genericErrorMessage())
    }

    yield put(AppActions.exception(error))
  }
}

export function* deletePrompt() {
  try {
    const i18n = yield select(Selectors.getI18n)
    const prompt = yield select(Selectors.getGaioPromptToDelete)
    const projectId = yield select(Selectors.getGaioSelectedProjectId)

    yield call(Api.deleteGaioPrompt, prompt.get('id'), { project_id: projectId })

    yield put(Actions.deletePromptSuccess())
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('success'),
      variant: 'success'
    }))
    yield put(Actions.searchPromptsStart())
    yield put(Actions.fetchPromptsStatisticsStart())
    yield put(Actions.fetchPromptOptionsStart())
  } catch (error) {
    yield put(Actions.deletePromptError(error))
    yield put(AppActions.genericErrorMessage())
    yield put(AppActions.exception(error))
  }
}

export function* requestSubscriptionChange({ payload }) {
  try {
    const i18n = yield select(Selectors.getI18n)

    yield call(Api.requestGaioSubscriptionChange, payload)

    yield put(Actions.requestSubscriptionChangeSuccess())
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('gaio_subscription_change_requested'),
      variant: 'success'
    }))
  } catch (error) {
    yield put(Actions.requestSubscriptionChangeError(error))
    yield put(AppActions.genericErrorMessage())
    yield put(AppActions.exception(error))
  }
}

export function* searchPromptsOnProjectSwitch() {
  yield put(FilterActions.setFilter({ field: 'gaioPrompts', values: List() }))

  const isPromptsOverview = yield select(Selectors.isGeoAnalysisPromptsOverview)

  if (isPromptsOverview) {
    yield put(Actions.searchPromptsStart())
  } else {
    yield put(Actions.fetchPromptOptionsStart())
  }
}

export function* watchCreateProject() {
  yield takeEvery(Actions.createProjectStart, createProject)
}

export function* watchUpdateProject() {
  yield takeEvery(Actions.updateProjectStart, updateProject)
}

export function* watchDeleteProject() {
  yield takeEvery(Actions.deleteProjectStart, deleteProject)
}

export function* watchCreatePrompt() {
  yield takeEvery(Actions.createPromptStart, createPrompt)
}

export function* watchUpdatePrompt() {
  yield takeEvery(Actions.updatePromptStart, updatePrompt)
}

export function* watchDeletePrompt() {
  yield takeEvery(Actions.deletePromptStart, deletePrompt)
}

export function* watchRequestSubscriptionChange() {
  yield takeEvery(Actions.requestSubscriptionChangeStart, requestSubscriptionChange)
}

export function* watchInitialLoad() {
  yield takeEvery(Actions.initialLoad, initialLoad)
}

export function* watchSetSelectedProject() {
  yield takeEvery(Actions.setSelectedProject, searchPromptsOnProjectSwitch)
}

export default function* mainSaga() {
  yield all([
    watchInitialLoad(),
    watchSetSelectedProject(),
    watchCreateProject(),
    watchUpdateProject(),
    watchDeleteProject(),
    watchCreatePrompt(),
    watchUpdatePrompt(),
    watchDeletePrompt(),
    watchRequestSubscriptionChange()
  ])
}
